import { AssetSubClassCode } from '@api/api/entity-constants';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { KeyValueElement } from './components/KeyValueElement';
import { useKeyValues } from './hooks/use-key-values/useKeyValues';
import {
  isKeyValueApplicable,
  isManufacturingPmi,
  type KeyValuesEntity,
} from './key-values';
import styles from './OverviewKeyValues.module.scss';

export interface OverviewKeyValuesProps {
  entityTag: string;
  entity?: KeyValuesEntity;
  isDefaultVariant?: boolean;
  isStandalone?: boolean;
}

export const OverviewKeyValues = ({
  entityTag,
  entity,
  isDefaultVariant = true,
  isStandalone = false,
}: OverviewKeyValuesProps) => {
  const { t } = useTranslation('widget');

  const { data, isError, isFetching, error } = useKeyValues({
    entityTag,
  });

  if (isFetching) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '164px',
              borderRadius: '8px',
            },
          },
        ]}
        data-testid="loading-state"
      />
    );
  }

  if (isError) {
    throw error;
  }

  const filteredData =
    data?.filter(value => isKeyValueApplicable(value, entity)) ?? [];

  if (!filteredData.length) {
    return null;
  }

  const metricLabels: Record<string, string> = {
    'bop_current_account_level.change_pct(horiz=1y)': t(
      'widget:assetOverview.keyValues.currentAccountChange',
      { defaultValue: 'Current account change (Y/Y)' }
    ),
    'yield_rsi(horiz=14d)': t('widget:assetOverview.keyValues.yieldRsi', {
      defaultValue: 'Yield RSI (14D)',
    }),
    'carry(fully_funded=yes)': t(
      'widget:assetOverview.keyValues.fullyFundedCarry',
      {
        defaultValue: 'Fully funded carry',
      }
    ),
    'cpi_core_level.change_pct(horiz=1y)': t(
      'widget:assetOverview.keyValues.coreCpiChange',
      {
        defaultValue: 'Core CPI change (Y/Y)',
      }
    ),
  };

  return (
    <div
      className={clsx(styles.overviewKeyValuesRoot, {
        [styles.standalone]: isStandalone,
        [styles.nonStock]:
          entity && entity.sub_class !== AssetSubClassCode.EquityStock,
      })}
      data-testid="overview-key-values"
    >
      {isDefaultVariant && (
        <SectionTitle title={t('widget:assetOverview.keyValuesTitle')} />
      )}
      <div className={styles.keyValuesList} data-key-values-list>
        {filteredData.map(keyValue => {
          const isNonStock =
            entity && entity.sub_class !== AssetSubClassCode.EquityStock;
          const isPmi =
            entity &&
            [AssetSubClassCode.EcoCountry, AssetSubClassCode.EcoArea].includes(
              entity.sub_class
            ) &&
            isManufacturingPmi(keyValue.key);
          let display = isNonStock
            ? (metricLabels[keyValue.key] ?? keyValue.display)
            : keyValue.display;
          if (isPmi) {
            const source = keyValue.key.split('.').at(-1) ?? '';
            const provider =
              keyValue.display.match(/\(([^)]+)\)/)?.[1] ??
              ({ ism: 'ISM', markit: 'Markit' } as Record<string, string>)[
                source
              ] ??
              source;
            display = t('widget:assetOverview.keyValues.manufacturingPmi', {
              provider,
            });
          }
          return (
            <KeyValueElement
              key={keyValue.key}
              tooltip={
                isPmi
                  ? t(
                      'widget:assetOverview.keyValues.manufacturingPmiExplanation'
                    )
                  : undefined
              }
              keyValue={{ ...keyValue, display }}
              isDefaultVariant={isDefaultVariant}
            />
          );
        })}
      </div>
    </div>
  );
};
